import { Socket } from '@/lib/api/Socket';
import { SportsbookSocketManager } from '@/lib/api/SportsbookSocketManager';
import type { URpcType } from '@/hooks/useSocketRPC';
import type { UEntityName } from '@/config/constants';

import { timeout } from './timeout';

let rpcCount = 0;
const DEFAULT_WS_TIMEOUT_MS = 15000;

/**
 * @description - Allows you to verify that a specific entity
 * has been subscribed to on the socket
 * @param { Object } params
 * @param { String } params.entityName - entity name
 * @param { Array<String> } params.ids - ids of entity to subscribe.
 * @param { Boolean } [params.resolveOnAbort=true] - when true, resolves gracefully on abort instead of rejecting
 * @param { { data: Object, type: EN.URpcType } } params.rpc - object with RPC data. If set, a 'rpc' request will be sent instead of default 'join'.
 * When set to `true`, the function will continue execution even if the entity name is not found in 'entityNames' from constants.
 * @return { Object } Object with resolved and rejected entities/ids from socket
 */
export async function getFromWebSocket({
  entityName,
  ids,
  rpc,
  signal,
  resolveOnAbort = true,
}: UGetFromWebSocketParams & { signal?: AbortSignal }): Promise<{
  resolved: object[];
  rejected: string[];
}> {
  const result: { resolved: object[]; rejected: string[] } = {
    rejected: [],
    resolved: [],
  };

  if (!rpc && ids.length === 0) {
    return result;
  }

  if (!Socket.sportsbookSocketManager) {
    throw new Error('Socket manager is not initialized');
  }

  const { signal: combinedSignal, cleanup: cleanupCombined } = composeAbortSignals([
    signal,
    abortSignalWithTimeout(DEFAULT_WS_TIMEOUT_MS),
  ]);

  // If already aborted before any request starts, skip entirely
  if (combinedSignal.aborted) {
    cleanupCombined();
    return result;
  }

  if (rpc) {
    const currentRpcCount = ++rpcCount;
    const { data: rpcData, type: rpcType } = rpc;

    try {
      if (combinedSignal.aborted) {
        cleanupCombined();
        return result;
      }

      const onAbort = () => {
        cleanupCombined();
        return result;
      };

      combinedSignal.addEventListener('abort', onAbort, { once: true });

      const rpcRes = await Socket.sportsbookSocketManager.getRPC(rpcType, {
        ...rpcData,
        reqId: currentRpcCount,
      });

      combinedSignal.removeEventListener('abort', onAbort);
      cleanupCombined();

      if (rpcRes) {
        if (Array.isArray(rpcRes)) {
          result.resolved = rpcRes;
        } else {
          result.resolved.push(rpcRes);
        }
      }
      return result;
    } catch (error) {
      cleanupCombined();
      console.warn(`Error: try to get 'rpc' ${rpcType} from WS. Reason ${error}`);
      return result;
    }
  }

  const currentEntityName = entityName as UEntityName;

  const idsToCompare = new Set(ids);

  const promise = new Promise<{ resolved: object[]; rejected: string[] }>((res, rej) => {
    const socketInstance = SportsbookSocketManager.getInstance();

    const { unregister, subscribe, on } = socketInstance.registerSubscriber({
      key: 'getFromWebSocket',
    });

    const resolver = async (data: any) => {
      const {
        type,
        data: { value },
      } = data;

      if (type === 'batch') {
        if (idsToCompare.has(value.lid)) {
          idsToCompare.delete(value.lid);
          result.resolved.push(value);
        }
      }

      if (type === 'error') {
        if (idsToCompare.has(value.id)) {
          idsToCompare.delete(value.id);
          result.rejected.push(value.id);
        }
      }

      if (!idsToCompare.size) {
        combinedSignal.removeEventListener('abort', onAbort);
        cleanupCombined();
        res(result);
        await timeout(2);
        unregister();
      }
    };

    const onAbort = () => {
      combinedSignal.removeEventListener('abort', onAbort);
      cleanupCombined();
      if (resolveOnAbort) {
        res(result);
      } else {
        rej('aborted');
      }
      unregister();
    };

    combinedSignal.addEventListener('abort', onAbort, { once: true });

    if (combinedSignal.aborted) {
      onAbort();
      return;
    }

    on(currentEntityName, resolver);
    subscribe(currentEntityName, ids);
  });

  // Do not send anything if aborted
  if (combinedSignal.aborted) {
    cleanupCombined();
    return result;
  }

  try {
    return await promise;
  } catch (error) {
    cleanupCombined();
    console.warn(`Error: try to get ${entityName} ${[...idsToCompare]} from WS. Reason ${error}`);
    return result;
  }
}

function abortSignalWithTimeout(timeoutMs: number): AbortSignal {
  const controller = new AbortController();
  setTimeout(() => controller.abort(), timeoutMs);
  return controller.signal;
}

/**
 * @description Composes multiple AbortSignals into one. Aborts when any of the provided signals aborts.
 * Returns a composed signal and a cleanup function to remove listeners.
 */
function composeAbortSignals(signals: Array<AbortSignal | undefined | null>): {
  signal: AbortSignal;
  cleanup: () => void;
} {
  const controller = new AbortController();
  const listeners: Array<() => void> = [];

  const abort = () => {
    if (!controller.signal.aborted) controller.abort();
  };

  for (const s of signals) {
    if (!s) continue;
    if (s.aborted) {
      abort();
      continue;
    }
    const onAbort = () => abort();
    s.addEventListener('abort', onAbort);
    listeners.push(() => s.removeEventListener('abort', onAbort));
  }

  const cleanup = () => {
    for (const off of listeners) off();
  };

  return { cleanup, signal: controller.signal };
}

// NOTE: We compose the external `signal` with a default timeout signal to centralize cancellation.

/* Types */
type UGetFromWebSocketParams =
  | {
      rpc: { data: object; type: URpcType };
      entityName?: never;
      ids?: never;
      resolveOnAbort?: never;
    }
  | {
      rpc?: never;
      entityName: UEntityName | UExtendedEntityName;
      ids: string[] | UWidgetName[];
      resolveOnAbort?: boolean;
    };

type ExtendedEntityNames = {
  widget: readonly ['justForYou', 'popularBets'];
};
type UExtendedEntityName = keyof ExtendedEntityNames;
type UWidgetName = ExtendedEntityNames['widget'][number];
